import React, { useEffect, useMemo, useState } from "react";
import { Bot, FileCheck2, KeyRound, Plus, ShieldCheck, Wrench } from "lucide-react";

import { apiPatch, apiPost } from "../../Api/Client.js";
import { errorText } from "../../Api/errorText.js";
import { Alert, Badge, Button, Card, Field, FormGrid, Input, Modal, Page, PageHeader, Select, Stat, StatGrid, Table, Tabs, Textarea } from "../../Ui/index.jsx";
import { findById, formatDateTime } from "../../Shared/ScreenUtils.jsx";
import "../../Styles/McpScreen.css";

const TABS = [
  { key: "agents", label: "Agents" },
  { key: "tools", label: "Tools" },
  { key: "resources", label: "Resources" },
  { key: "grants", label: "Access grants" },
  { key: "audits", label: "Invocation audits" },
  { key: "drafts", label: "Draft actions" },
];

const nameOf = (row) => row.display_name || row.name;
const options = (rows) => (rows || []).map((row) => ({ value: row.id, label: nameOf(row) }));

export function McpScreen({ data, reload }) {
  const [tab, setTab] = useState("agents");
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [invokeAgent, setInvokeAgent] = useState(null);
  const [draftFor, setDraftFor] = useState(null);

  const agents = data.agentPrincipals || [];
  const tools = data.mcpToolDefinitions || [];
  const resources = data.mcpResourceDefinitions || [];
  const grants = data.mcpAccessGrants || [];
  const audits = data.mcpInvocationAudits || [];
  const drafts = data.draftAgentActions || [];

  const refresh = () => reload(["agentPrincipals", "mcpToolDefinitions", "mcpResourceDefinitions", "mcpAccessGrants", "mcpInvocationAudits", "draftAgentActions"]);

  const [notice, setNotice] = useState("");

  // The app loads none of these six lists at start-up (they are not among its first-load lists), so this tab
  // asks for them itself when it opens; without it every count here stayed at zero until something was saved.
  useEffect(() => { refresh(); }, []);

  // The draft's address is a detail route: it takes PATCH, not POST, and only the status changes.
  const approveDraft = async (action) => {
    setNotice("");
    try {
      await apiPatch(`/McpAccessLayer/DraftAgentActions/${action.id}/`, { status: "Approved" });
      reload(["draftAgentActions"]);
    } catch (error) {
      setNotice(errorText(error, "Could not approve this draft. Please try again."));
    }
  };

  const stats = useMemo(() => ({
    agents: agents.length,
    tools: tools.length,
    resources: resources.length,
    grants: grants.length,
    audits: audits.length,
    drafts: drafts.filter((draft) => draft.status === "Pending" || draft.status === "Draft").length,
  }), [agents, tools, resources, grants, audits, drafts]);

  const agentName = (id) => { const agent = findById(agents, id); return agent ? nameOf(agent) : id; };

  return (
    <Page>
      <PageHeader
        title="MCP agents & access"
        subtitle="Govern agent principals, tool definitions, resources and audit trails."
        actions={(
          <>
            <Button variant="primary" leftIcon={<Plus size={14} />} onClick={() => setCreateAgentOpen(true)}>New agent</Button>
            <Button leftIcon={<KeyRound size={14} />} onClick={() => setGrantOpen(true)}>Grant access</Button>
          </>
        )}
      />

      {notice && <Alert tone="danger">{notice}</Alert>}

      <StatGrid>
        <Stat label="Agent principals" value={stats.agents} />
        <Stat label="Tool definitions" value={stats.tools} />
        <Stat label="Resources" value={stats.resources} />
        <Stat label="Access grants" value={stats.grants} />
        <Stat label="Invocation audits" value={stats.audits} />
        <Stat label="Pending drafts" value={stats.drafts} />
      </StatGrid>

      <Tabs id="mcp" label="MCP sections" tabs={TABS} value={tab} onChange={setTab} />

      <div role="tabpanel" id="mcp-tabpanel" aria-labelledby={`mcp-tab-${tab}`}>
        {tab === "agents" && (
          <Card title="Agent principals">
            <Table flush stack empty="No agent principals yet." rows={agents} columns={[
              { key: "agent", header: "Agent", className: "is-card-title", render: (agent) => (
                <>
                  <span className="Mcp-Line"><Bot size={14} aria-hidden="true" /> <span className="ds-strong">{agent.display_name || agent.name}</span></span>
                  <span className="ds-small Mcp-Line">{agent.principal_type || "Agent"}</span>
                </>
              ) },
              { key: "owner", header: "Owner", render: (agent) => agent.owner_name || agent.owner || "-" },
              { key: "status", header: "Status", className: "is-card-amount", render: (agent) => <Badge variant={agent.status === "Active" ? "success" : "warning"}>{agent.status || "Inactive"}</Badge> },
              { key: "created", header: "Created", render: (agent) => formatDateTime(agent.created_at) },
              { key: "actions", header: "Actions", className: "is-card-actions", render: (agent) => (
                <span className="ds-toolbar">
                  <Button size="sm" onClick={() => setInvokeAgent(agent)}>Invoke</Button>
                  <Button size="sm" onClick={() => setDraftFor(agent)}>Draft action</Button>
                </span>
              ) },
            ]} />
          </Card>
        )}

        {tab === "tools" && (
          <Card title="Tool definitions">
            <Table flush stack empty="No tools registered." rows={tools} columns={[
              { key: "tool", header: "Tool", className: "is-card-title", render: (tool) => tool.display_name || tool.name },
              { key: "provider", header: "Provider", render: (tool) => tool.provider || "Internal" },
              { key: "permission", header: "Permission", render: (tool) => tool.permission || "Read" },
              { key: "status", header: "Status", className: "is-card-amount", render: (tool) => <Badge variant={tool.is_active ? "success" : "warning"}>{tool.is_active ? "Active" : "Inactive"}</Badge> },
            ]} />
          </Card>
        )}

        {tab === "resources" && (
          <Card title="Resource definitions">
            <Table flush stack empty="No resources registered." rows={resources} columns={[
              { key: "resource", header: "Resource", className: "is-card-title", render: (res) => res.display_name || res.name },
              { key: "type", header: "Type", render: (res) => res.resource_type || "-" },
              { key: "owner", header: "Owner", render: (res) => res.owner_name || "-" },
              { key: "visibility", header: "Visibility", className: "is-card-amount", render: (res) => res.visibility || "Tenant" },
            ]} />
          </Card>
        )}

        {tab === "grants" && (
          <Card title="Access grants" actions={<Button size="sm" variant="primary" leftIcon={<Plus size={13} />} onClick={() => setGrantOpen(true)}>New grant</Button>}>
            <Table flush stack empty="No grants yet." rows={grants} columns={[
              { key: "agent", header: "Agent", className: "is-card-title", render: (grant) => agentName(grant.agent) },
              { key: "tool", header: "Tool", render: (grant) => findById(tools, grant.tool)?.display_name || grant.tool || "*" },
              { key: "resource", header: "Resource", render: (grant) => findById(resources, grant.resource)?.display_name || grant.resource || "*" },
              { key: "permission", header: "Permission", className: "is-card-amount", render: (grant) => grant.permission || "Read" },
              { key: "expires", header: "Expires", render: (grant) => (grant.expires_at ? formatDateTime(grant.expires_at) : "Never") },
            ]} />
          </Card>
        )}

        {tab === "audits" && (
          <Card title="Recent invocations">
            <Table flush stack empty="No invocation audits yet." rows={audits.slice(0, 50)} columns={[
              { key: "when", header: "When", className: "is-card-title", render: (row) => formatDateTime(row.created_at) },
              { key: "agent", header: "Agent", render: (row) => agentName(row.agent) },
              { key: "action", header: "Action" },
              { key: "decision", header: "Decision", className: "is-card-amount", render: (row) => <Badge variant={row.decision === "Allowed" ? "success" : "danger"}>{row.decision}</Badge> },
              { key: "reason", header: "Reason", render: (row) => row.reason || "-" },
            ]} />
          </Card>
        )}

        {tab === "drafts" && (
          <Card title="Draft agent actions">
            <Table flush stack empty="No draft actions." rows={drafts} columns={[
              { key: "agent", header: "Agent", className: "is-card-title", render: (draft) => agentName(draft.agent) },
              { key: "type", header: "Action type", render: (draft) => <span><FileCheck2 size={13} aria-hidden="true" /> {draft.action_type}</span> },
              { key: "target", header: "Target", render: (draft) => <span>{draft.target_resource_type} {draft.target_resource_id && `#${draft.target_resource_id}`}</span> },
              { key: "status", header: "Status", className: "is-card-amount", render: (draft) => <Badge variant={draft.status === "Approved" ? "success" : draft.status === "Rejected" ? "danger" : "warning"}>{draft.status || "Pending"}</Badge> },
              { key: "created", header: "Created", render: (draft) => formatDateTime(draft.created_at) },
              { key: "actions", header: "Actions", className: "is-card-actions", render: (draft) => (
                draft.status !== "Approved" && draft.status !== "Rejected" ? <Button size="sm" onClick={() => approveDraft(draft)}>Approve</Button> : null
              ) },
            ]} />
          </Card>
        )}
      </div>

      {createAgentOpen && <CreateAgentModal data={data} onClose={() => setCreateAgentOpen(false)} reload={refresh} />}
      {grantOpen && <CreateGrantModal data={data} onClose={() => setGrantOpen(false)} reload={refresh} />}
      {invokeAgent && <InvokeAgentModal agent={invokeAgent} data={data} onClose={() => setInvokeAgent(null)} reload={refresh} />}
      {draftFor && <DraftActionModal agent={draftFor} onClose={() => setDraftFor(null)} reload={refresh} />}
    </Page>
  );
}

const set = (form, setForm, key) => (event) => setForm({ ...form, [key]: event.target.value });

function CreateAgentModal({ data, onClose, reload }) {
  const [form, setForm] = useState({ name: "", display_name: "", principal_type: "ServiceAccount", status: "Active", owner: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await apiPost("/McpAccessLayer/AgentPrincipals/", form);
      reload();
      onClose();
    } catch (e) {
      setError(errorText(e, "Could not create this agent. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New agent principal" onClose={onClose} size="lg">
      <div className="Mcp-Form">
        <FormGrid>
          <Field label="Name"><Input value={form.name} onChange={set(form, setForm, "name")} placeholder="Agent-Bot" /></Field>
          <Field label="Display name"><Input value={form.display_name} onChange={set(form, setForm, "display_name")} /></Field>
          <Field label="Type"><Select value={form.principal_type} onChange={set(form, setForm, "principal_type")} options={["ServiceAccount", "User", "System"]} /></Field>
          <Field label="Status"><Select value={form.status} onChange={set(form, setForm, "status")} options={["Active", "Suspended"]} /></Field>
          <Field label="Owner"><Select value={form.owner} onChange={set(form, setForm, "owner")} placeholder="No owner" options={(data.employees || []).map((emp) => ({ value: emp.id, label: emp.display_name }))} /></Field>
        </FormGrid>
        {error && <Alert tone="danger">{error}</Alert>}
        <div><Button variant="primary" onClick={save} disabled={busy || !form.name}>Create agent</Button></div>
      </div>
    </Modal>
  );
}

function CreateGrantModal({ data, onClose, reload }) {
  const [form, setForm] = useState({ agent: "", tool: "", resource: "", permission: "Read", expires_at: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.tool) delete payload.tool;
      if (!payload.resource) delete payload.resource;
      if (!payload.expires_at) delete payload.expires_at;
      await apiPost("/McpAccessLayer/McpAccessGrants/", payload);
      reload();
      onClose();
    } catch (e) {
      setError(errorText(e, "Could not save this grant. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New access grant" onClose={onClose} size="lg">
      <div className="Mcp-Form">
        <FormGrid>
          <Field label="Agent"><Select value={form.agent} onChange={set(form, setForm, "agent")} placeholder="Select" options={options(data.agentPrincipals)} /></Field>
          <Field label="Tool"><Select value={form.tool} onChange={set(form, setForm, "tool")} placeholder="Any tool" options={options(data.mcpToolDefinitions)} /></Field>
          <Field label="Resource"><Select value={form.resource} onChange={set(form, setForm, "resource")} placeholder="Any resource" options={options(data.mcpResourceDefinitions)} /></Field>
          <Field label="Permission"><Select value={form.permission} onChange={set(form, setForm, "permission")} options={["Read", "Write", "Admin"]} /></Field>
          <Field label="Expires at"><Input type="datetime-local" value={form.expires_at} onChange={set(form, setForm, "expires_at")} /></Field>
        </FormGrid>
        {error && <Alert tone="danger">{error}</Alert>}
        <div><Button variant="primary" onClick={save} disabled={busy || !form.agent}>Grant access</Button></div>
      </div>
    </Modal>
  );
}

function InvokeAgentModal({ agent, data, onClose, reload }) {
  const [form, setForm] = useState({ tool: "", resource: "", action: "Invoke", reason: "" });
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const checkPermission = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiPost(`/McpAccessLayer/AgentPrincipals/${agent.id}/can-invoke/`, { tool: form.tool || null, resource: form.resource || null, permission: "Read" });
      setDecision(res?.allowed === true ? "Allowed" : "Denied");
    } catch (e) {
      setError(errorText(e, "Could not check this permission. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const recordInvocation = async () => {
    setBusy(true);
    setError("");
    try {
      await apiPost(`/McpAccessLayer/AgentPrincipals/${agent.id}/record-invocation/`, {
        tool: form.tool || null,
        resource: form.resource || null,
        action: form.action,
        decision: decision || "Allowed",
        reason: form.reason,
      });
      reload();
      onClose();
    } catch (e) {
      setError(errorText(e, "Could not record this invocation. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Invoke as ${agent.display_name || agent.name}`} onClose={onClose} size="lg">
      <div className="Mcp-Form">
        <FormGrid>
          <Field label="Tool"><Select value={form.tool} onChange={set(form, setForm, "tool")} placeholder="Any" options={options(data.mcpToolDefinitions)} /></Field>
          <Field label="Resource"><Select value={form.resource} onChange={set(form, setForm, "resource")} placeholder="Any" options={options(data.mcpResourceDefinitions)} /></Field>
          <Field label="Action"><Input value={form.action} onChange={set(form, setForm, "action")} /></Field>
          <Field label="Reason"><Input value={form.reason} onChange={set(form, setForm, "reason")} /></Field>
        </FormGrid>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="ds-toolbar">
          <Button size="sm" onClick={checkPermission} disabled={busy} leftIcon={<ShieldCheck size={13} />}>Check permission</Button>
          {decision && <Badge variant={decision === "Allowed" ? "success" : "danger"}>{decision}</Badge>}
        </div>
        <div><Button variant="primary" onClick={recordInvocation} disabled={busy} leftIcon={<Wrench size={13} />}>Record invocation</Button></div>
      </div>
    </Modal>
  );
}

function DraftActionModal({ agent, onClose, reload }) {
  const [form, setForm] = useState({ action_type: "Create", target_resource_type: "WorkItem", target_resource_id: "", payload: "{}" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    // A payload that is not valid JSON is refused here: it must never become a draft someone approves later.
    let payload;
    try { payload = JSON.parse(form.payload || "{}"); } catch { setError("The payload is not valid JSON. Please correct it."); return; }
    setBusy(true);
    try {
      await apiPost(`/McpAccessLayer/AgentPrincipals/${agent.id}/draft-action/`, {
        action_type: form.action_type,
        target_resource_type: form.target_resource_type,
        target_resource_id: form.target_resource_id,
        payload,
      });
      reload();
      onClose();
    } catch (e) {
      setError(errorText(e, "Could not create this draft. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Draft action for ${agent.display_name || agent.name}`} onClose={onClose} size="lg">
      <div className="Mcp-Form">
        <FormGrid>
          <Field label="Action type"><Input value={form.action_type} onChange={set(form, setForm, "action_type")} /></Field>
          <Field label="Target type"><Input value={form.target_resource_type} onChange={set(form, setForm, "target_resource_type")} /></Field>
          <Field label="Target id"><Input value={form.target_resource_id} onChange={set(form, setForm, "target_resource_id")} /></Field>
        </FormGrid>
        <Field label="Payload (JSON)"><Textarea rows={6} value={form.payload} onChange={set(form, setForm, "payload")} /></Field>
        {error && <Alert tone="danger">{error}</Alert>}
        <div><Button variant="primary" onClick={save} disabled={busy}>Create draft</Button></div>
      </div>
    </Modal>
  );
}
